import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { usePrefs, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../utilities/getTargetLocations';
import ConditionalWrapper from '../ConditionalWrapper';
import { selectIsTabbedLayout } from '../../features/layoutSlice';

const Callout = ({ callouts, vehicle }) => {
	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const { useSimplifiedListing } = useSelector((state) => state.layout);
	const { widgetName, windowId } = useRequestData();

	// This section allows us to use a preference to determine which vehicles should get the regular
	// WIAPI insert locations and which ones should have 'custom' locations added instead.
	const { restrictInsertLocationsForVehicles } = usePrefs();

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);

	const vehicleBadgeLocation = getVehicleLocationName(
		'vehicle-badge',
		targetLocations,
		restrictInsertLocationsForVehicles
	);
	const vehicleMediaLocation = getVehicleLocationName(
		'vehicle-media',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const badgeName = 'vehicle-card-badge';

	const calloutItems = [];

	if (targetLocations.size === 0 || !targetLocations.has('vehicle-ctas')) {
		calloutItems.push(
			...callouts.map(
				(
					{
						imageSrc,
						imageAlt,
						imageTitle,
						badgeClasses = [],
						href = null,
						target = null,
						tooltip = ''
					},
					index
				) => {
					// image element needs a height/width set for svg logo to render correctly
					const isCarfax = badgeClasses.includes('carfax');
					const carfaxLogoAttr = isCarfax ? '100vw' : undefined;
					const imageInitHeight = !isCarfax ? '30px' : undefined;
					const imageStyles = { aspectRatio: 'auto 8 / 3' };

					const img = (
						<img
							src={imageSrc}
							width={carfaxLogoAttr}
							height={imageInitHeight}
							alt={imageAlt}
							title={imageTitle}
							style={imageStyles}
						/>
					);

					const hasTooltip = !!tooltip && typeof tooltip === 'string';
					/* eslint-disable react/no-danger */
					const tooltipMarkup = (
						<Popover id="popover">
							<span
								dangerouslySetInnerHTML={{ __html: tooltip }}
							/>
						</Popover>
					);
					/* eslint-enable react/no-danger */

					const decodedHref =
						href !== null ? href.replace(/&amp;/g, '&') : '';

					const markup = href ? (
						<a
							href={decodedHref}
							className={setClassNames([...badgeClasses])}
							target={target}
							onClick={() =>
								trackEvent(widgetName, windowId, {
									element: 'Badge button',
									elementCTA: imageTitle
								})
							}
							rel={
								href.startsWith('http') ? 'nofollow' : undefined
							}
						>
							{img}
						</a>
					) : (
						img
					);

					return (
						<li
							key={imageTitle}
							className={setClassNames([
								badgeName,
								...badgeClasses,
								'py-3',
								useSimplifiedListing && index === 0
									? 'pl-0 my-2 ml-0 mr-2'
									: 'm-2'
							])}
						>
							<ConditionalWrapper
								condition={hasTooltip}
								wrapper={(children) => (
									<OverlayTrigger
										placement="top"
										overlay={tooltipMarkup}
									>
										{children}
									</OverlayTrigger>
								)}
							>
								{markup}
							</ConditionalWrapper>
						</li>
					);
				}
			)
		);
	}

	const badgeWrapperClasses = [
		'list-unstyled',
		'd-flex',
		'flex-wrap',
		'align-items-center',
		!useSimplifiedListing ? 'justify-content-center' : ''
	];

	if (calloutItems.length === 0) {
		badgeWrapperClasses.push('hidden');
	}

	return (
		<div
			className={setClassNames([
				'badge-callout',
				calloutItems.length === 0 ? 'py-3' : ''
			])}
		>
			<ul
				className={setClassNames(badgeWrapperClasses)}
				data-location={
					isTabbedLayout
						? vehicleBadgeLocation
						: `${vehicleMediaLocation} ${vehicleBadgeLocation}`
				}
			>
				{calloutItems}
			</ul>
		</div>
	);
};

Callout.propTypes = {
	callouts: PropTypes.arrayOf(PropTypes.shape({})),
	vehicle: PropTypes.shape({})
};

Callout.defaultProps = {
	callouts: [],
	vehicle: {}
};

export default Callout;
